// 利益レポート・仕入先レポート（Stocky parity v2 Step 8）。読み取り専用で何も書かない。
//
// 🔴 除外件数を必ず画面に出す。「原価未設定の商品を利益100%として混ぜない」ために
//    サーバが除外しているので、その事実を隠すと合計が実感と合わず、マーチャントは
//    数字ごと信用しなくなる（空を説明するときは行き先も添える＝設定画面へ誘導）。
// 🔴 原価は「現在の設定値」であることを明記する＝過去の確定利益に見せない。
import {
  Banner,
  BlockStack,
  Button,
  Card,
  DataTable,
  InlineStack,
  Layout,
  Page,
  SkeletonBodyText,
  Text,
  TextField,
} from "@shopify/polaris";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { getProfitReport, getSupplierReport } from "../api/reports";
import { getPlanPreview } from "../api/plan_previews";
import { GatedQueryError } from "../components/GatedQueryError";
import { PlanDataPreviewGate } from "../components/PlanDataPreviewGate";

import { t } from "../lib/i18n";
import { toLocalIsoDate } from "../utils/date";

/** 直近30日を既定の期間にする（サーバ側の既定と揃える）。
 *
 * 🔴 日付は **ローカルタイム** で組み立てる。`toISOString()` は UTC の日付を返すので、
 *    JST の店では 00:00〜09:00 のあいだ終了日が「昨日」になり、その日の売上が
 *    黙って範囲外へ落ちる（実測で確認した実バグ）。
 */
function defaultPeriod(): { start: string; end: string } {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - 29);
  return { start: toLocalIsoDate(start), end: toLocalIsoDate(end) };
}

function formatRate(rate: number | null): string {
  return rate === null ? "—" : `${Math.round(rate * 100)}%`;
}

export function Reports() {
  const initial = defaultPeriod();
  const [startInput, setStartInput] = useState(initial.start);
  const [endInput, setEndInput] = useState(initial.end);
  const [period, setPeriod] = useState(initial);

  return (
    <Page title={t("レポート")} subtitle={t("利益と仕入先の実績を期間で見ます。")}>
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            <PlanDataPreviewGate
              feature="reports"
              featureName={t("レポート")}
              previewQueryKey={["plan-preview", "reports"]}
              previewQueryFn={() =>
                Promise.all([
                  getPlanPreview("report_profit_inputs_30d"),
                  getPlanPreview("report_supplier_inputs_30d"),
                ])
              }
              sections={[
                {
                  dataset: "report_profit_inputs_30d",
                  title: t("直近30日・利益レポートの集計元明細"),
                  columns: [
                    { key: "product", label: t("商品") },
                    { key: "sku", label: "SKU" },
                    { key: "quantity", label: t("販売数") },
                    { key: "revenue", label: t("売上") },
                    { key: "cost", label: t("原価") },
                    { key: "profit", label: t("粗利") },
                    { key: "margin", label: t("粗利率") },
                  ],
                },
                {
                  dataset: "report_supplier_inputs_30d",
                  title: t("直近30日・仕入先レポートの集計元発注書"),
                  columns: [
                    { key: "supplier", label: t("仕入先") },
                    { key: "purchase_order", label: t("発注書") },
                    { key: "ordered_on", label: t("発注日") },
                    { key: "expected_on", label: t("入荷予定日") },
                    { key: "item_count", label: t("明細数") },
                    { key: "ordered_amount", label: t("発注金額") },
                    { key: "status", label: t("ステータス") },
                  ],
                },
              ]}
            >
              <BlockStack gap="400">
                <Card>
                  <InlineStack gap="300" blockAlign="end" wrap>
                    <div style={{ width: 180 }}>
                      <TextField
                        label={t("開始日")}
                        type="date"
                        autoComplete="off"
                        value={startInput}
                        onChange={setStartInput}
                      />
                    </div>
                    <div style={{ width: 180 }}>
                      <TextField
                        label={t("終了日")}
                        type="date"
                        autoComplete="off"
                        value={endInput}
                        onChange={setEndInput}
                      />
                    </div>
                    <Button
                      onClick={() =>
                        setPeriod({ start: startInput, end: endInput })
                      }
                      disabled={
                        startInput === "" ||
                        endInput === "" ||
                        endInput < startInput
                      }
                    >
                      {t("この期間で表示")}
                    </Button>
                  </InlineStack>
                  {endInput < startInput && (
                    <Text as="p" tone="critical" variant="bodySm">
                      {t("終了日は開始日以降を指定してください。")}
                    </Text>
                  )}
                </Card>
                <ProfitSection period={period} />
                <SupplierSection period={period} />
              </BlockStack>
            </PlanDataPreviewGate>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

function ProfitSection({ period }: { period: { start: string; end: string } }) {
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["reports", "profit", period.start, period.end],
    queryFn: () => getProfitReport(period),
  });

  if (isLoading) return <SkeletonBodyText lines={4} />;
  if (isError || !data) {
    return <GatedQueryError title={t("利益レポート")} onRetry={() => void refetch()} />;
  }

  const excluded = data.excluded_no_cost_lines + data.excluded_unsynced_lines;
  // 🔴 表は上位N件で切られる。合計は全商品ぶんなので、黙っていると
  //    「表の行を足しても合計に合わない」状態になる（除外件数と同じ理由で必ず出す）。
  //    列が無い旧 API（`undefined`）では打ち切りを主張しない。
  const isTruncated =
    data.row_count_total !== undefined && data.row_count_total > data.rows.length;

  return (
    <Card>
      <BlockStack gap="300">
        <Text as="h2" variant="headingMd">
          {t("利益（商品別）")}
        </Text>
        <InlineStack gap="500" wrap>
          <Text as="span">
            {t("売上")}: {data.revenue_total}
          </Text>
          <Text as="span">
            {t("原価")}: {data.cost_total}
          </Text>
          <Text as="span" fontWeight="semibold">
            {t("粗利")}: {data.profit_total}
          </Text>
        </InlineStack>

        {excluded > 0 && (
          <Banner
            tone="warning"
            title={t("{n} 件の明細を集計から除外しました", { n: excluded })}
            action={
              data.excluded_no_cost_lines > 0
                ? {
                    content: t("原価を設定する"),
                    onAction: () => navigate("/inventory-analytics"),
                  }
                : undefined
            }
          >
            <BlockStack gap="100">
              {data.excluded_no_cost_lines > 0 && (
                <Text as="p" variant="bodySm">
                  {t("原価が未設定の商品: {n} 件。原価を入れないと粗利が実態より小さく出るため、集計に混ぜていません。", {
                    n: data.excluded_no_cost_lines,
                  })}
                </Text>
              )}
              {data.excluded_unsynced_lines > 0 && (
                <Text as="p" variant="bodySm">
                  {t("売上がまだ同期されていない明細: {n} 件。次回の同期で集計に入ります。", {
                    n: data.excluded_unsynced_lines,
                  })}
                </Text>
              )}
            </BlockStack>
          </Banner>
        )}

        {data.rows.length === 0 ? (
          // 空表の理由は2つに割れる＝①この期間に売れていない ②売れたが原価未設定・
          // 未同期で全部除外された。②は上の警告と重ねて読むと分かるが、行き先が
          // 無いと手が止まる（空状態には必ず行き先を添える）。
          <Banner
            tone="info"
            title={t("この期間に集計できる売上がありません")}
            action={{
              content: t("原価を設定する"),
              onAction: () => navigate("/inventory-analytics"),
            }}
          >
            <p>
              {excluded > 0
                ? t("この期間の明細はすべて集計対象外でした（原価未設定・売上が未同期）。原価を設定すると粗利が出ます。")
                : t("この期間に売れた商品がありません。期間を広げるか、同期が終わっているかをご確認ください。")}
            </p>
          </Banner>
        ) : (
          <DataTable
            columnContentTypes={["text", "numeric", "numeric", "numeric", "numeric", "numeric"]}
            headings={[
              t("商品"),
              t("販売数"),
              t("売上"),
              t("原価"),
              t("粗利"),
              t("粗利率"),
            ]}
            rows={data.rows.map((row) => [
              row.title ?? row.sku ?? t("（商品名なし）"),
              row.quantity.toLocaleString(),
              row.revenue,
              row.cost,
              row.profit,
              row.margin_pct === null ? "—" : `${row.margin_pct}%`,
            ])}
          />
        )}

        {isTruncated && (
          <Text as="p" variant="bodySm" tone="subdued">
            {t("上位 {shown} 件を表示しています（対象 {total} 件）。上の合計は表に出ていない商品も含めた全体の金額です。", {
              shown: data.rows.length,
              total: data.row_count_total as number,
            })}
          </Text>
        )}

        <Text as="p" variant="bodySm" tone="subdued">
          {t("原価は現在の設定値を使っています（Shopify は注文明細に仕入原価を持たないため）。原価を後から変えると過去の粗利も変わります。")}
        </Text>
      </BlockStack>
    </Card>
  );
}

function SupplierSection({ period }: { period: { start: string; end: string } }) {
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["reports", "suppliers", period.start, period.end],
    queryFn: () => getSupplierReport(period),
  });

  if (isLoading) return <SkeletonBodyText lines={4} />;
  if (isError || !data) {
    return <GatedQueryError title={t("仕入先レポート")} onRetry={() => void refetch()} />;
  }

  return (
    <Card>
      <BlockStack gap="300">
        <Text as="h2" variant="headingMd">
          {t("仕入先")}
        </Text>
        {data.rows.length === 0 ? (
          // 仕入先レポートは「注文済みとしてマーク」した発注書だけを集計する＝
          // 下書きのまま運用している店はここが永久に空になる。理由と行き先を出す。
          <Banner
            tone="info"
            title={t("この期間に確定した発注がありません")}
            action={{
              content: t("注文書を開く"),
              onAction: () => navigate("/purchase-orders"),
            }}
          >
            <p>
              {t("このレポートは「注文済みとしてマーク」した発注書だけを集計します。下書きのままの発注書は含まれません。")}
            </p>
          </Banner>
        ) : (
          <DataTable
            columnContentTypes={["text", "numeric", "numeric", "numeric", "numeric"]}
            headings={[
              t("仕入先"),
              t("発注件数"),
              t("発注金額"),
              t("平均リードタイム"),
              t("納期どおり"),
            ]}
            rows={data.rows.map((row) => [
              row.supplier_name ?? t("（仕入先の指定なし）"),
              row.order_count.toLocaleString(),
              row.ordered_amount,
              row.avg_lead_time_days === null
                ? "—"
                : t("{n} 日", { n: row.avg_lead_time_days }),
              formatRate(row.on_time_rate),
            ])}
          />
        )}
        <Text as="p" variant="bodySm" tone="subdued">
          {t("平均リードタイムは発注確定から最初の入荷までの日数です。入荷の実績が無い仕入先は「—」と表示します（0日とは書きません）。")}
        </Text>
      </BlockStack>
    </Card>
  );
}
